import React, { useCallback, useContext, useEffect, useRef, useState } from "react";

import { useParams, useHistory } from "react-router-dom";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  Paper,
  Tab,
  Tabs,
  TextField,
} from "@material-ui/core";
import { toast } from "react-toastify";
import ChatList from "./ChatList";
import ChatMessages from "./ChatMessages";
import { UsersFilter } from "../../components/UsersFilter";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";
import logo from "../../assets/logo.png";

import { has, isObject } from "lodash";

import { AuthContext } from "../../context/Auth/AuthContext";
import withWidth, { isWidthUp } from "@material-ui/core/withWidth";

// Mesmas medidas da tela de atendimentos (pages/TicketsCustom): coluna esquerda
// de 450px fixos no desktop, conversa ocupando o resto.
const useStyles = makeStyles((theme) => ({
  chatContainer: {
    flex: 1,
    padding: theme.spacing(1),
    height: `calc(100% - 48px)`,
    overflowY: "hidden",
  },
  chatPapper: {
    display: "flex",
    height: "100%",
  },
  contactsWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflowY: "hidden",
    maxWidth: "100%",
    flexBasis: "100%",
    [theme.breakpoints.up("md")]: {
      width: 450,
      minWidth: 450,
      maxWidth: 450,
      flexBasis: 450,
    },
  },
  messagesWrapper: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    maxWidth: "100%",
    flexBasis: "100%",
    [theme.breakpoints.up("md")]: {
      maxWidth: "calc(100% - 450px)",
      flexBasis: "calc(100% - 450px)",
    },
  },
  welcomeMsg: {
    backgroundColor: theme.palette.boxticket,
    display: "flex",
    justifyContent: "space-evenly",
    alignItems: "center",
    height: "100%",
    textAlign: "center",
  },
  tabsWrapper: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
}));

export function ChatModal({
  open,
  chat,
  type,
  handleClose,
  handleLoadNewChat,
}) {
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState("");

  useEffect(() => {
    setTitle("");
    setUsers([]);
    if (type === "edit") {
      const userList = chat.users.map((u) => ({
        id: u.user.id,
        name: u.user.name,
      }));
      setUsers(userList);
      setTitle(chat.title);
    }
  }, [chat, open, type]);

  const handleSave = async () => {
    try {
      if (!title) {
        alert(i18n.t("internalChat.modal.titleRequired"));
        return;
      }

      if (!users || users.length === 0) {
        alert(i18n.t("internalChat.modal.usersRequired"));
        return;
      }

      if (type === "edit") {
        await api.put(`/chats/${chat.id}`, {
          users,
          title,
        });
      } else {
        const { data } = await api.post("/chats", {
          users,
          title,
        });
        handleLoadNewChat(data);
      }
      handleClose();
    } catch (err) {}
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{i18n.t("internalChat.modal.title")}</DialogTitle>
      <DialogContent>
        <Grid spacing={2} container>
          <Grid xs={12} style={{ padding: 18 }} item>
            <TextField
              label={i18n.t("internalChat.modal.titleField")}
              placeholder={i18n.t("internalChat.modal.titleField")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid xs={12} item>
            <UsersFilter
              onFiltered={(users) => setUsers(users)}
              initialUsers={users}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {i18n.t("common.close")}
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          {i18n.t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Chat(props) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();

  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState("new");
  const [currentChat, setCurrentChat] = useState({});
  const [chats, setChats] = useState([]);
  const [chatsPageInfo, setChatsPageInfo] = useState({ hasMore: false });
  const [messages, setMessages] = useState([]);
  const [messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [loading, setLoading] = useState(false);
  // Carga de historico e envio de mensagem sao coisas diferentes: com um unico
  // `loading`, enviar uma mensagem bloqueava o loadMore e ainda acendia o spinner
  // de historico no topo da lista.
  const [loadingMore, setLoadingMore] = useState(false);
  const [tab, setTab] = useState(0);
  // Alvo de resposta (citacao). Estado local de proposito: o ReplyingMessageContext
  // so envolve a subarvore do chat de tickets, nunca o App, e os formatos de
  // mensagem sao diferentes — nao ha nada de fato compartilhavel.
  const [replyingMessage, setReplyingMessage] = useState(null);
  // Pedido de salto para uma mensagem. O token muda a cada pedido para que saltar
  // duas vezes para a MESMA mensagem dispare o efeito de novo.
  const [jumpRequest, setJumpRequest] = useState({ id: null, token: 0 });
  const isMounted = useRef(true);
  const scrollToBottomRef = useRef();
  // Evita duas cargas de historico simultaneas (o state de loading so atualizaria
  // no proximo render).
  const historyInFlightRef = useRef(false);
  // Sequencia das cargas ancoradas: resposta atrasada nao pode sobrescrever a
  // lista de uma conversa que ja mudou.
  const jumpSeqRef = useRef(0);
  // Espelho do id da conversa aberta, para uso dentro de handlers de socket.
  const currentChatIdRef = useRef(null);
  const { id } = useParams();

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    currentChatIdRef.current = currentChat?.id ?? null;
  }, [currentChat?.id]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      findChats().then((data) => {
        if (!data) return;
        const { records } = data;
        if (records.length > 0) {
          setChats(records);
          setChatsPageInfo(data);

          if (id && records.length) {
            const chat = records.find((r) => r.uuid === id);
            if (chat) selectChat(chat);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isObject(currentChat) && has(currentChat, "id")) {
      findMessages(currentChat.id).then(() => {
        setTimeout(() => {
          scrollToBottomRef.current?.({ force: true });
        }, 300);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat?.id]);

  // Coloca a conversa que recebeu mensagem no topo, acompanhando a ordenacao por
  // updatedAt que o backend passou a usar (ChatService/ListService).
  const upsertChatOnTop = useCallback((incomingChat) => {
    if (!incomingChat) return;
    setChats((prev) => {
      const rest = prev.filter((c) => c.id !== incomingChat.id);
      const previous = prev.find((c) => c.id === incomingChat.id);
      return [{ ...previous, ...incomingChat }, ...rest];
    });
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-chat-user-${user.id}`, (data) => {
      if (data.action === "create") {
        setChats((prev) => [data.record, ...prev]);
      }
      if (data.action === "update") {
        setChats((prev) =>
          prev.map((chat) => (chat.id === data.record.id ? data.record : chat))
        );
        setCurrentChat((prev) =>
          prev?.id === data.record.id ? data.record : prev
        );
      }
    });

    socket.on(`company-${companyId}-chat`, (data) => {
      if (data.action === "delete") {
        setChats((prev) => prev.filter((c) => c.id !== +data.id));
        // Comparado pelo ref e nao dentro do setState: efeito colateral
        // (navegacao) dentro de um updater roda duas vezes em StrictMode.
        if (currentChatIdRef.current === +data.id) {
          setMessages([]);
          setMessagesPageInfo({ hasMore: false });
          setReplyingMessage(null);
          setCurrentChat({});
          history.push("/chats");
        }
      }
    });

    if (isObject(currentChat) && has(currentChat, "id")) {
      socket.on(`company-${companyId}-chat-${currentChat.id}`, (data) => {
        if (data.action === "new-message") {
          setMessages((prev) =>
            prev.some((m) => m.id === data.newMessage.id)
              ? prev
              : [...prev, data.newMessage]
          );
          upsertChatOnTop(data.chat);
          // Só arrasta para o fim se o usuário já estava lá, ou se a mensagem é
          // dele. Sem isso, quem está lendo o histórico depois de um salto de
          // pesquisa era jogado para o fim a cada mensagem recebida.
          scrollToBottomRef.current?.({
            force: data.newMessage?.senderId === user.id,
          });
        }

        if (data.action === "update") {
          upsertChatOnTop(data.chat);
        }
      });
    }

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat?.id, socketManager, upsertChatOnTop]);

  const selectChat = (chat) => {
    try {
      // Reclicar na conversa já aberta não pode limpar as mensagens: o efeito que
      // recarrega o histórico observa currentChat.id, que não muda nesse caso, e a
      // conversa ficaria vazia sem nunca mais recarregar.
      if (chat?.id && chat.id === currentChat?.id) {
        setTab(1);
        return;
      }

      setMessages([]);
      setMessagesPageInfo({ hasMore: false });
      setReplyingMessage(null);
      setJumpRequest({ id: null, token: 0 });
      setCurrentChat(chat);
      setTab(1);
    } catch (err) {}
  };

  const sendMessage = async (contentMessage, files = [], quotedMsgId = null) => {
    setLoading(true);
    try {
      if (files.length > 0) {
        const formData = new FormData();
        // typeArch precisa ir ANTES dos arquivos: o multer le req.body.typeArch
        // no meio do stream para decidir a pasta de destino (config/upload.ts).
        formData.append("typeArch", "internalChat");
        formData.append("message", contentMessage || "");
        if (quotedMsgId) {
          formData.append("quotedMsgId", String(quotedMsgId));
        }
        files.forEach((file) => formData.append("medias", file));
        await api.post(`/chats/${currentChat.id}/messages`, formData);
      } else {
        await api.post(`/chats/${currentChat.id}/messages`, {
          message: contentMessage,
          quotedMsgId: quotedMsgId || undefined,
        });
      }
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const deleteChat = async (chat) => {
    try {
      await api.delete(`/chats/${chat.id}`);
    } catch (err) {}
  };

  // Paginacao por keyset: a proxima pagina e "as 20 anteriores a esta id".
  // Numero de pagina nao serve aqui — createdAt tem precisao de segundo (varias
  // mensagens no mesmo instante) e, depois de um salto ancorado que carrega
  // centenas de mensagens, nenhum indice de pagina esta correto.
  const findMessages = async (chatId, beforeId) => {
    if (historyInFlightRef.current) return;
    historyInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const query = beforeId ? `?beforeId=${beforeId}` : "";
      const { data } = await api.get(`/chats/${chatId}/messages${query}`);
      setMessagesPageInfo({ hasMore: data.hasMore });
      setMessages((prev) =>
        beforeId ? [...data.records, ...prev] : data.records
      );
    } catch (err) {
      toastError(err);
    }
    historyInFlightRef.current = false;
    setLoadingMore(false);
  };

  const loadMoreMessages = async () => {
    if (historyInFlightRef.current || !currentChat?.id) return;
    const oldestId = messages[0]?.id;
    if (!oldestId) return;
    findMessages(currentChat.id, oldestId);
  };

  // Leva a conversa ate uma mensagem especifica (clique numa citacao ou resultado
  // da pesquisa). Se ela ja esta carregada, so pede a centralizacao; senao busca o
  // historico ancorado nela.
  const jumpToMessage = async (messageId) => {
    if (!messageId || !currentChat?.id) return;

    const target = String(messageId);

    if (messages.some((m) => String(m.id) === target)) {
      setJumpRequest({ id: target, token: Date.now() });
      return;
    }

    const seq = ++jumpSeqRef.current;
    const chatId = currentChat.id;
    setLoadingMore(true);

    try {
      const { data } = await api.get(
        `/chats/${chatId}/messages/around/${messageId}`
      );

      // Resposta atrasada de uma conversa que ja nao esta aberta.
      if (seq !== jumpSeqRef.current || currentChatIdRef.current !== chatId) {
        return;
      }

      if (data.beyondLimit) {
        toast.warning(i18n.t("internalChat.jump.tooOld"));
        return;
      }

      if (!data.records?.length) {
        toast.warning(i18n.t("internalChat.jump.notFound"));
        return;
      }

      setMessages(data.records);
      setMessagesPageInfo({ hasMore: data.hasMore });
      setJumpRequest({ id: target, token: Date.now() });
    } catch (err) {
      toast.warning(i18n.t("internalChat.jump.notFound"));
    } finally {
      setLoadingMore(false);
    }
  };

  const findChats = async () => {
    try {
      const { data } = await api.get("/chats");
      return data;
    } catch (err) {
      console.log(err);
    }
  };

  const openNewChatDialog = () => {
    setDialogType("new");
    setShowDialog(true);
  };

  const openEditChatDialog = () => {
    setDialogType("edit");
    setShowDialog(true);
  };

  const renderChatList = () => (
    <ChatList
      chats={chats}
      pageInfo={chatsPageInfo}
      loading={loading}
      handleNewChat={openNewChatDialog}
      handleSelectChat={(chat) => selectChat(chat)}
    />
  );

  const renderChatMessages = () => (
    <ChatMessages
      chat={currentChat}
      scrollToBottomRef={scrollToBottomRef}
      pageInfo={messagesPageInfo}
      messages={messages}
      loading={loading}
      loadingMore={loadingMore}
      replyingMessage={replyingMessage}
      setReplyingMessage={setReplyingMessage}
      jumpRequest={jumpRequest}
      onJumpToMessage={jumpToMessage}
      handleSendMessage={sendMessage}
      handleLoadMore={loadMoreMessages}
      handleEditChat={openEditChatDialog}
      handleDeleteChat={deleteChat}
    />
  );

  const hasCurrentChat = isObject(currentChat) && has(currentChat, "id");

  const renderGrid = () => {
    return (
      <div className={classes.chatContainer}>
        <div className={classes.chatPapper}>
          <Grid container spacing={0}>
            <Grid item xs className={classes.contactsWrapper}>
              {renderChatList()}
            </Grid>
            <Grid item xs className={classes.messagesWrapper}>
              {hasCurrentChat ? (
                renderChatMessages()
              ) : (
                <Paper square variant="outlined" className={classes.welcomeMsg}>
                  <div>
                    <center>
                      <img
                        style={{ margin: "0 auto", width: "70%" }}
                        src={logo}
                        alt="logologin"
                      />
                    </center>
                  </div>
                </Paper>
              )}
            </Grid>
          </Grid>
        </div>
      </div>
    );
  };

  const renderTab = () => {
    return (
      <div className={classes.chatContainer}>
        <div className={classes.tabsWrapper}>
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            onChange={(e, v) => setTab(v)}
          >
            <Tab label={i18n.t("internalChat.tabs.chats")} />
            <Tab label={i18n.t("internalChat.tabs.messages")} />
          </Tabs>
          {tab === 0 && <div className={classes.tabPanel}>{renderChatList()}</div>}
          {tab === 1 && (
            <div className={classes.tabPanel}>
              {hasCurrentChat && renderChatMessages()}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <ChatModal
        type={dialogType}
        open={showDialog}
        chat={currentChat}
        handleLoadNewChat={(data) => {
          setMessages([]);
          setReplyingMessage(null);
          setCurrentChat(data);
          setTab(1);
          history.push(`/chats/${data.uuid}`);
        }}
        handleClose={() => setShowDialog(false)}
      />
      {isWidthUp("md", props.width) ? renderGrid() : renderTab()}
    </>
  );
}

export default withWidth()(Chat);
